import { useCurrentRoleStore } from '~/stores/currentRole'

const prefix = 'wlearn-view-v1:'
const lifetime = 24 * 60 * 60 * 1000

/** Browser-only snapshots, isolated by account, school and role. */
export function useLocalDataCache() {
  const user = useSupabaseUser()
  const { currentRole } = storeToRefs(useCurrentRoleStore())
  const scope = () =>
    JSON.stringify([
      user.value?.id,
      currentRole.value?.schoolId,
      currentRole.value?.role,
    ])
  const key = (name: string) => prefix + scope() + ':' + name
  const read = <T>(name: string): T | undefined => {
    if (!import.meta.client || !user.value?.id) return
    try {
      const storageKey = key(name)
      const raw = localStorage.getItem(storageKey)
      if (!raw) return
      const entry = JSON.parse(raw)
      if (
        !Number.isFinite(entry.savedAt) ||
        Date.now() - entry.savedAt >= lifetime ||
        entry.savedAt > Date.now()
      ) {
        localStorage.removeItem(storageKey)
        return
      }
      return entry.data as T
    } catch {
      return
    }
  }
  const write = (name: string, data: unknown, expectedScope = scope()) => {
    if (!import.meta.client || !user.value?.id || scope() !== expectedScope)
      return
    try {
      localStorage.setItem(
        key(name),
        JSON.stringify({ savedAt: Date.now(), data }),
      )
    } catch {
      /* Storage may be disabled or full; fetching still works. */
    }
  }
  const clear = () => {
    if (!import.meta.client) return
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(prefix))
        .forEach((key) => localStorage.removeItem(key))
    } catch {
      /* Storage may be disabled. */
    }
  }
  return { read, write, clear, scope }
}
